import React, { useState, useEffect, useCallback } from 'react';
import { botAPI, userAPI } from '../services/api';
import { getDiceBearUserAvatar, getDiceBearGroupAvatar } from '../services/dicebear';
import { getAvatarStyle } from '../utils/avatar';
import { TEXT_COLORS } from '../utils/textColors';
import Keywords from './Keywords';
import { BUTTON_COLORS } from '../utils/buttonColors';
import CharacterCounter from '../utils/characterCounter';
import { STACK_COLORS } from '../utils/stackColors';

const BotProfile = ({ botId, onBack, onClose }) => {
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [tagInputError, setTagInputError] = useState('');
  const [themeVibes, setThemeVibes] = useState([]);
  const [themeVibeInput, setThemeVibeInput] = useState('');
  const [savingThemeVibes, setSavingThemeVibes] = useState(false);
  const [showPurgeThemeVibesDialog, setShowPurgeThemeVibesDialog] = useState(false);
  const [themeVibeInputError, setThemeVibeInputError] = useState('');
  const [positions, setPositions] = useState([]);
  const [positionsOffset, setPositionsOffset] = useState(0);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsSaving, setPositionsSaving] = useState(false);
  const [operations, setOperations] = useState([]);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsError, setOperationsError] = useState('');
  const [operationDetail, setOperationDetail] = useState(null);
  const [operationDialogOpen, setOperationDialogOpen] = useState(false);
  const [operationDialogLoading, setOperationDialogLoading] = useState(false);
  const [operationDialogError, setOperationDialogError] = useState('');
  const [operationRunDialogOpen, setOperationRunDialogOpen] = useState(false);
  const [operationKeywords, setOperationKeywords] = useState(['', '', '']);
  const [operationOpenPositions, setOperationOpenPositions] = useState(3);
  const [operationThemeTone, setOperationThemeTone] = useState('');
  const [operationStackPreviews, setOperationStackPreviews] = useState(3);
  const [operationRunError, setOperationRunError] = useState('');
  const [operationRunLoading, setOperationRunLoading] = useState(false);
  // DATA_SET bot operation state
  const [operationStackName, setOperationStackName] = useState('');
  const [operationAffinityMatching, setOperationAffinityMatching] = useState(false);
  const [operationTransformationId, setOperationTransformationId] = useState(null);
  const [operationSelectedDataSetIds, setOperationSelectedDataSetIds] = useState([]);
  const [operationSelectedFilterIds, setOperationSelectedFilterIds] = useState([]);
  const [operationFailError, setOperationFailError] = useState('');
  const [operationFailLoading, setOperationFailLoading] = useState(false);
  const [operationRequestExpanded, setOperationRequestExpanded] = useState(false);
  const [operationResponseExpanded, setOperationResponseExpanded] = useState(false);
  const OPERATION_STATUS_COLORS = {
    STARTED: '#9e9e9e',
    TRIGGERED: '#9e9e9e',
    RUNNING: '#ffb300',
    FAILED: '#f44336',
    COMPLETED: '#4caf50',
  };

  // Regex pattern for keyword validation (same as backend)
  const validateKeywordFormat = (keyword) => {
    const pattern = /^[^(,]*?(?:\([^)]+\))?$/;
    return pattern.test(keyword);
  };

  const loadOperations = useCallback(async () => {
    setOperationsLoading(true);
    setOperationsError('');
    try {
      const response = await botAPI.getBotOperations(botId);
      setOperations(response.data || []);
    } catch (err) {
      console.error('Failed to load operations:', err);
      setOperationsError('Failed to load operations');
      setOperations([]);
    } finally {
      setOperationsLoading(false);
    }
  }, [botId]);

  const loadPositions = useCallback(async (offset = 0) => {
    setPositionsLoading(true);
    try {
      const response = await botAPI.getBotPositions(botId, offset, 3, true);
      // Initialize positions with defaults - the API will return the correct structure
      // based on bot type, so we just need to ensure defaults are set
      const positionsWithDefaults = response.data.map(pos => {
        // Check if this is a DATA_SET bot position by looking for array_affinity field
        if (pos.hasOwnProperty('array_affinity')) {
          // For DATA_SET bots, use different defaults
          return {
            ...pos,
            weight: pos.weight || 100,
            array_affinity: pos.array_affinity || null
          };
        } else {
          // For other bots, use existing defaults
          return {
            ...pos,
            weight: pos.weight || 100,
            context: pos.context || ''
          };
        }
      });
      setPositions(positionsWithDefaults);
      setPositionsOffset(offset);
    } catch (err) {
      console.error('Failed to load positions:', err);
      setError('Failed to load positions');
    } finally {
      setPositionsLoading(false);
    }
  }, [botId]);

  const refreshBot = useCallback(async () => {
    const response = await botAPI.getBot(botId);
    setBot(response.data);
    const tagsWithOriginal = (response.data.keywords || []).map(tag => ({
      ...tag,
      originalStatus: tag.status
    }));
    setTags(tagsWithOriginal);
    const themeVibesWithOriginal = (response.data.theme_tones || []).map(themeVibe => ({
      ...themeVibe,
      originalStatus: themeVibe.status
    }));
    setThemeVibes(themeVibesWithOriginal);
  }, [botId]);

  const handlePositionContextChange = (index, value) => {
    // Enforce 48 character limit
    if (value.length > 48) {
      return; // Don't update if over limit
    }
    setPositions(prev => prev.map((pos, i) => 
      i === index ? { ...pos, context: value } : pos
    ));
  };

  const handlePositionWeightChange = (index, weight) => {
    setPositions(prev => prev.map((pos, i) => 
      i === index ? { ...pos, weight } : pos
    ));
  };

  const handlePositionAffinityChange = (index, affinity) => {
    setPositions(prev => prev.map((pos, i) => 
      i === index ? { ...pos, array_affinity: affinity } : pos
    ));
  };

  const handlePositionAllowBotChange = (index, allowBot) => {
    setPositions(prev => prev.map((pos, i) => 
      i === index ? { ...pos, allow_bot: allowBot } : pos
    ));
  };

  const handleCreatorClick = (userId) => {
    if (!userId) return;
    try {
      window.dispatchEvent(new CustomEvent('openUser', { detail: { userId } }));
    } catch (_) {}
  };

  const handleOperationKeywordChange = (index, value) => {
    if (value.length > 64) return;
    setOperationKeywords((prev) =>
      prev.map((kw, i) => (i === index ? value : kw))
    );
  };

  const handleOperationOpenPositionsChange = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return;
    }
    setOperationOpenPositions(numeric);
  };

  const resetOperationForm = () => {
    if (bot?.type === 'DATA_SET') {
      // Reset DATA_SET bot form
      setOperationStackName('');
      setOperationAffinityMatching(false);
      setOperationTransformationId(null);
      // Default to all data sets selected
      const allDataSetIds = (bot.data_sets || []).map(ds => ds.id);
      setOperationSelectedDataSetIds(allDataSetIds);
      // Default to all available keywords selected
      const availableKeywords = (bot.keywords || []).filter(kw => kw.is_available);
      const allFilterIds = availableKeywords.map(kw => kw.id);
      setOperationSelectedFilterIds(allFilterIds);
      // Set default transformation to first one
      if (bot.transformations && bot.transformations.length > 0) {
        setOperationTransformationId(bot.transformations[0].id);
      }
      setOperationStackPreviews(3);
    } else {
      // Reset USER_DRIVEN bot form
      setOperationKeywords(['', '', '']);
      setOperationOpenPositions(3);
      setOperationThemeTone('');
      setOperationStackPreviews(3);
    }
    setOperationRunError('');
  };

  const openOperationRunDialog = () => {
    resetOperationForm();
    setOperationRunDialogOpen(true);
  };

  const closeOperationRunDialog = () => {
    setOperationRunDialogOpen(false);
    setOperationRunError('');
  };

  const handleRunOperation = async () => {
    if (!bot) {
      return;
    }
    
    if (bot.type === 'DATA_SET') {
      // Validate DATA_SET bot operation
      if (operationStackPreviews < 1 || operationStackPreviews > 8) {
        setOperationRunError('Stack previews must be between 1 and 8.');
        return;
      }
      if (!operationTransformationId) {
        setOperationRunError('Please select a transformation.');
        return;
      }
      if (operationSelectedDataSetIds.length === 0) {
        setOperationRunError('Please select at least one data set.');
        return;
      }
      
      setOperationRunLoading(true);
      setOperationRunError('');
      try {
        const payload = {
          stack_previews: Math.trunc(operationStackPreviews),
          theme: operationStackName.trim() || null,
          affinity_matching: operationAffinityMatching,
          transformation_id: operationTransformationId,
          data_set_ids: operationSelectedDataSetIds.length === (bot.data_sets || []).length 
            ? null 
            : operationSelectedDataSetIds,
          filter_ids: operationSelectedFilterIds.length === (bot.keywords || []).filter(kw => kw.is_available).length
            ? null
            : operationSelectedFilterIds,
        };
        await botAPI.createBotOperation(botId, payload);
        closeOperationRunDialog();
        await loadOperations();
        await refreshBot();
      } catch (err) {
        console.error('Failed to run operation:', err);
        const detail = err?.response?.data?.detail;
        let message = 'Failed to queue operation';
        if (Array.isArray(detail)) {
          message = detail
            .map((item) => item?.msg || JSON.stringify(item))
            .join('; ');
        } else if (typeof detail === 'string') {
          message = detail;
        } else if (detail && typeof detail === 'object') {
          message = detail.msg || JSON.stringify(detail);
        }
        setOperationRunError(message);
      } finally {
        setOperationRunLoading(false);
      }
    } else if (bot.type === 'USER_DRIVEN') {
      // Validate USER_DRIVEN bot operation
      if (
        (operationOpenPositions !== null &&
          (operationOpenPositions < 0 || operationOpenPositions > 6)) ||
        operationStackPreviews < 1 ||
        operationStackPreviews > 8
      ) {
        setOperationRunError('Please ensure open positions (0-6) and stack previews (1-8) are valid.');
        return;
      }
      if (operationThemeTone && operationThemeTone.length > 200) {
        setOperationRunError('Theme vibe must be 200 characters or fewer.');
        return;
      }
      setOperationRunLoading(true);
      setOperationRunError('');
      try {
        const keywords = operationKeywords
          .map((kw) => kw.trim())
          .filter((kw) => kw.length > 0);
        const payload = {};
        if (keywords.length > 0) {
          payload.keywords_prompt = keywords;
        }
        // Apply transformation: 6 - {input} to get open_positions value
        payload.open_positions = Math.trunc(6 - operationOpenPositions);
        const trimmedThemeTone = operationThemeTone.trim();
        payload.theme_tone = trimmedThemeTone ? trimmedThemeTone : null;
        if (
          typeof operationStackPreviews === 'number' &&
          !Number.isNaN(operationStackPreviews)
        ) {
          payload.stack_previews = Math.trunc(operationStackPreviews);
        }
        await botAPI.createBotOperation(botId, payload);
        closeOperationRunDialog();
        await loadOperations();
        await refreshBot();
      } catch (err) {
        console.error('Failed to run operation:', err);
        const detail = err?.response?.data?.detail;
        let message = 'Failed to queue operation';
        if (Array.isArray(detail)) {
          message = detail
            .map((item) => item?.msg || JSON.stringify(item))
            .join('; ');
        } else if (typeof detail === 'string') {
          message = detail;
        } else if (detail && typeof detail === 'object') {
          message = detail.msg || JSON.stringify(detail);
        }
        setOperationRunError(message);
      } finally {
        setOperationRunLoading(false);
      }
    }
  };

  const shouldShowExecuteButton = Boolean(bot?.can_execute);

  const formatDateTime = (value) => {
    if (!value) return 'Unknown';
    try {
      return new Date(value).toLocaleString();
    } catch (_) {
      return value;
    }
  };

  const renderOperationStatus = (status) => {
    const normalized = (status || '').toUpperCase();
    const color = OPERATION_STATUS_COLORS[normalized] || '#9e9e9e';
    const label = normalized ? normalized.replace('_', ' ').toLowerCase() : 'unknown';
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '999px',
          backgroundColor: `${color}20`,
          color,
          fontSize: '12px',
          fontWeight: 'bold',
          textTransform: 'capitalize',
        }}
      >
        {label}
      </span>
    );
  };

  const canFailOperation = (() => {
    if (!operationDetail || !currentUser) {
      return false;
    }
    const createdMs = Date.parse(operationDetail.created_at);
    if (Number.isNaN(createdMs)) {
      return false;
    }
    const olderThanHour = Date.now() - createdMs >= 60 * 60 * 1000;
    if (!olderThanHour) {
      return false;
    }
    const status = (operationDetail.status || '').toUpperCase();
    if (status === 'FAILED' || status === 'COMPLETED') {
      return false;
    }
    const isCreator =
      operationDetail.creator && operationDetail.creator.id === currentUser.id;
    const isAdmin = currentUser.role === 'ADMIN';
    return Boolean(isCreator || isAdmin);
  })();

  const handleFailOperation = async () => {
    if (!operationDetail) {
      return;
    }
    setOperationFailLoading(true);
    setOperationFailError('');
    try {
      await botAPI.failBotOperation(botId, operationDetail.id);
      await loadOperations();
      await fetchOperationDetail(operationDetail.id);
      await refreshBot();
    } catch (err) {
      console.error('Failed to fail operation:', err);
      const detail = err?.response?.data?.detail;
      let message = 'Failed to mark operation as failed';
      if (Array.isArray(detail)) {
        message = detail
          .map((item) => item?.msg || JSON.stringify(item))
          .join('; ');
      } else if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object') {
        message = detail.msg || JSON.stringify(detail);
      }
      setOperationFailError(message);
    } finally {
      setOperationFailLoading(false);
    }
  };

  const fetchOperationDetail = useCallback(
    async (operationId) => {
      const response = await botAPI.getBotOperationDetail(botId, operationId);
      setOperationDetail(response.data);
      return response.data;
    },
    [botId]
  );

  const handleOpenOperationDialog = async (operationId) => {
    setOperationDialogOpen(true);
    setOperationDialogLoading(true);
    setOperationDialogError('');
    setOperationRequestExpanded(false);
    setOperationResponseExpanded(false);
    setOperationFailError('');
    try {
      await fetchOperationDetail(operationId);
    } catch (err) {
      console.error('Failed to load operation detail:', err);
      setOperationDialogError(err.response?.data?.detail || 'Failed to load operation');
    } finally {
      setOperationDialogLoading(false);
    }
  };

  const handleCloseOperationDialog = () => {
    setOperationDialogOpen(false);
    setOperationDetail(null);
    setOperationDialogError('');
    setOperationRequestExpanded(false);
    setOperationResponseExpanded(false);
    setOperationFailError('');
  };


  const handleSkipPositions = async () => {
    await loadPositions(positionsOffset + 3);
  };

  const handleSavePositions = async () => {
    setPositionsSaving(true);
    try {
      let positionsToSave;
      
      // Check if this is a DATA_SET bot by looking for array_affinity field in positions
      const isDataSetBot = positions.length > 0 && positions[0].hasOwnProperty('array_affinity');
      
      if (isDataSetBot) {
        // For DATA_SET bots, only save positions where user has selected a color affinity
        positionsToSave = positions
          .filter(pos => pos.array_affinity !== null && pos.array_affinity !== undefined)
          .map(pos => ({
            name: pos.name,
            weight: pos.weight,
            array_affinity: pos.array_affinity
          }));
      } else {
        // For other bots, use existing structure
        positionsToSave = positions.map(pos => ({
          stack_position_id: pos.stack_position_id,
          position_name: pos.position_name,
          context: pos.context || null,
          weight: pos.weight,
          allow_bot: pos.allow_bot || false
        }));
      }

      await botAPI.upsertBotPositions(botId, positionsToSave);
      
      // Reload the same page after successful save
      await loadPositions(positionsOffset);
    } catch (err) {
      console.error('Failed to save positions:', err);
      setError('Failed to save positions');
    } finally {
      setPositionsSaving(false);
    }
  };

  useEffect(() => {
    const loadBot = async () => {
      try {
        setLoading(true);
        await refreshBot();
        await loadOperations();
      } catch (err) {
        setError('Failed to load bot');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadBot();
  }, [botId, loadOperations, refreshBot]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await userAPI.getCurrentUser();
        setCurrentUser(response.data);
      } catch (err) {
        console.error('Failed to load current user:', err);
      }
    };

    loadCurrentUser();
  }, []);

  // Load positions when current user is loaded and is admin
  useEffect(() => {
    if (currentUser && (currentUser.role === 'ADMIN' || (bot && currentUser.id === bot.owner_id))) {
      loadPositions();
    }
  }, [currentUser, bot, loadPositions]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setTagInput(value);
    
    // Clear error when user starts typing
    if (tagInputError) {
      setTagInputError('');
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      addTagFromInput();
    }
  };

  const handleTagInputBlur = () => {
    if (tagInput.trim()) {
      addTagFromInput();
    }
  };

  const handleThemeVibeInputChange = (e) => {
    const value = e.target.value;
    setThemeVibeInput(value);
    
    // Clear error when user starts typing
    if (themeVibeInputError) {
      setThemeVibeInputError('');
    }
  };

  const handleThemeVibeInputKeyDown = (e) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      addThemeVibeFromInput();
    }
  };

  const handleThemeVibeInputBlur = () => {
    if (themeVibeInput.trim()) {
      addThemeVibeFromInput();
    }
  };

  const addTagFromInput = () => {
    const newTagName = tagInput.trim();
    if (newTagName) {
      // Validate tag length
      if (newTagName.length > 48) {
        setTagInputError('Tag name must be 48 characters or less');
        return;
      }
      
      // Validate keyword format
      if (!validateKeywordFormat(newTagName)) {
        setTagInputError('Invalid tag format. Tags must match pattern: text with optional (context) at the end. Commas are not allowed.');
        return;
      }
      
      // Check if tag already exists
      const existingTag = tags.find(tag => tag.name === newTagName);
      
      if (existingTag) {
        // If it's a deactivated tag, reactivate it to pending
        if (existingTag.status === 'DISABLED' && !existingTag.isRemoved) {
          setTags(tags.map(tag => 
            tag.id === existingTag.id 
              ? { ...tag, status: 'PENDING', isNew: true, isRemoved: false }
              : tag
          ));
        }
        // If it's already active/pending, do nothing
      } else {
        // Create new tag
        const newTag = {
          id: `temp-${Date.now()}`,
          name: newTagName,
          status: 'PENDING',
          isNew: true,
          originalStatus: 'PENDING' // New tags have no original state
        };
        setTags([...tags, newTag]);
      }
      setTagInput('');
      setTagInputError(''); // Clear any previous errors
    }
  };

  const addThemeVibeFromInput = () => {
    const newThemeVibeName = themeVibeInput.trim();
    if (newThemeVibeName) {
      // Validate theme vibe length
      if (newThemeVibeName.length > 48) {
        setThemeVibeInputError('Theme vibe name must be 48 characters or less');
        return;
      }
      
      // Validate keyword format (same validation as tags)
      if (!validateKeywordFormat(newThemeVibeName)) {
        setThemeVibeInputError('Invalid theme vibe format. Theme vibes must match pattern: text with optional (context) at the end. Commas are not allowed.');
        return;
      }
      
      // Check if theme vibe already exists
      const existingThemeVibe = themeVibes.find(themeVibe => themeVibe.name === newThemeVibeName);
      
      if (existingThemeVibe) {
        // If it's a deactivated theme vibe, reactivate it to pending
        if (existingThemeVibe.status === 'DISABLED' && !existingThemeVibe.isRemoved) {
          setThemeVibes(themeVibes.map(themeVibe => 
            themeVibe.id === existingThemeVibe.id 
              ? { ...themeVibe, status: 'PENDING', isNew: true, isRemoved: false }
              : themeVibe
          ));
        }
        // If it's already active/pending, do nothing
      } else {
        // Create new theme vibe
        const newThemeVibe = {
          id: `temp-${Date.now()}`,
          name: newThemeVibeName,
          status: 'PENDING',
          isNew: true,
          originalStatus: 'PENDING' // New theme vibes have no original state
        };
        setThemeVibes([...themeVibes, newThemeVibe]);
      }
      setThemeVibeInput('');
      setThemeVibeInputError(''); // Clear any previous errors
    }
  };

  const removeTag = (tagId) => {
    setTags(tags.map(tag => {
      if (tag.id === tagId) {
        // Prevent removal of non-editable tags
        if (tag.is_editable === false) {
          return tag;
        }
        if (tag.isNew) {
          // Handle new tags (including reactivated DISABLED tags)
          if (tag.originalStatus === 'DISABLED') {
            // If this was a reactivated DISABLED tag, toggle back to DISABLED
            return { 
              ...tag, 
              status: 'DISABLED', 
              isRemoved: false,
              isNew: false
            };
          } else {
            // Remove completely new tags
            return null;
          }
        } else {
          // Handle existing tags
          if (tag.isRemoved) {
            // If already marked for removal, toggle back to original state
            return { 
              ...tag, 
              status: tag.originalStatus, 
              isRemoved: false,
              isNew: false
            };
          } else if (tag.status === 'DISABLED') {
            // If DISABLED tag is clicked, reactivate it (turn yellow)
            return { 
              ...tag, 
              status: 'PENDING', 
              isRemoved: false,
              isNew: true
            };
          } else {
            // Mark existing active tags for deletion with pending removal state
            return { ...tag, status: 'DEACTIVATING', isRemoved: true };
          }
        }
      }
      return tag;
    }).filter(Boolean));
  };

  const removeThemeVibe = (themeVibeId) => {
    setThemeVibes(themeVibes.map(themeVibe => {
      if (themeVibe.id === themeVibeId) {
        // Prevent removal of non-editable theme vibes
        if (themeVibe.is_editable === false) {
          return themeVibe;
        }
        if (themeVibe.isNew) {
          // Handle new theme vibes (including reactivated DISABLED theme vibes)
          if (themeVibe.originalStatus === 'DISABLED') {
            // If this was a reactivated DISABLED theme vibe, toggle back to DISABLED
            return { 
              ...themeVibe, 
              status: 'DISABLED', 
              isRemoved: false,
              isNew: false
            };
          } else {
            // Remove completely new theme vibes
            return null;
          }
        } else {
          // Handle existing theme vibes
          if (themeVibe.isRemoved) {
            // If already marked for removal, toggle back to original state
            return { 
              ...themeVibe, 
              status: themeVibe.originalStatus, 
              isRemoved: false,
              isNew: false
            };
          } else if (themeVibe.status === 'DISABLED') {
            // If DISABLED theme vibe is clicked, reactivate it (turn yellow)
            return { 
              ...themeVibe, 
              status: 'PENDING', 
              isRemoved: false,
              isNew: true
            };
          } else {
            // Mark existing active theme vibes for deletion with pending removal state
            return { ...themeVibe, status: 'DEACTIVATING', isRemoved: true };
          }
        }
      }
      return themeVibe;
    }).filter(Boolean));
  };

  const getTagColor = (tag) => {
    // Non-editable tags are always blue
    if (tag.is_editable === false) return '#2196f3'; // blue
    
    if (tag.isNew) return '#ffeb3b'; // yellow
    if (tag.isRemoved) return '#ff9800'; // orange (pending removal)
    switch (tag.status) {
      case 'PENDING': return '#ffeb3b'; // yellow
      case 'ACTIVE': return '#4caf50'; // green
      case 'DEACTIVATING': return '#ff9800'; // orange
      case 'DISABLED': return '#f44336'; // red
      default: return '#9e9e9e'; // gray
    }
  };

  const getThemeVibeColor = (themeVibe) => {
    // Non-editable theme vibes are always blue
    if (themeVibe.is_editable === false) return '#2196f3'; // blue
    
    if (themeVibe.isNew) return '#ffeb3b'; // yellow
    if (themeVibe.isRemoved) return '#ff9800'; // orange (pending removal)
    switch (themeVibe.status) {
      case 'PENDING': return '#ffeb3b'; // yellow
      case 'ACTIVE': return '#4caf50'; // green
      case 'DEACTIVATING': return '#ff9800'; // orange
      case 'DISABLED': return '#f44336'; // red
      default: return '#9e9e9e'; // gray
    }
  };

  const getTagStyle = (tag) => {
    const baseStyle = {
      backgroundColor: getTagColor(tag),
      color: tag.status === 'PENDING' ? '#000' : '#fff',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    };

    // Add italic styling for unavailable tags while keeping the background color
    if (tag.is_available === false) {
      baseStyle.fontStyle = 'italic';
    }

    return baseStyle;
  };

  const getThemeVibeStyle = (themeVibe) => {
    const baseStyle = {
      backgroundColor: getThemeVibeColor(themeVibe),
      color: themeVibe.status === 'PENDING' ? '#000' : '#fff',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    };

    // Add italic styling for unavailable theme vibes while keeping the background color
    if (themeVibe.is_available === false) {
      baseStyle.fontStyle = 'italic';
    }

    return baseStyle;
  };

  const handleSaveTags = async () => {
    try {
      setSaving(true);
      setError(''); // Clear any previous errors
      
      // Get new tags (yellow) - those that are actually new or reactivated from DISABLED
      const newTags = tags.filter(tag => tag.isNew && tag.status === 'PENDING' && tag.is_editable !== false);
      // Get removed tags (orange) - only those that are actually marked for removal
      const removedTags = tags.filter(tag => tag.isRemoved && tag.status === 'DEACTIVATING' && tag.is_editable !== false);

      console.log('Saving tags:', { newTags, removedTags });

      // Add new tags
      if (newTags.length > 0) {
        const newTagNames = newTags.map(tag => tag.name);
        console.log('Adding new tags:', newTagNames);
        await botAPI.addBotKeyword(botId, { keywords: newTagNames });
      }

      // Remove tags
      if (removedTags.length > 0) {
        const removedTagNames = removedTags.map(tag => tag.name);
        console.log('Removing tags:', removedTagNames);
        await botAPI.removeBotKeyword(botId, { keywords: removedTagNames });
      }

      // Refresh bot data
      const response = await botAPI.getBot(botId);
      setBot(response.data);
      // Add originalStatus to track the initial state
      const tagsWithOriginal = (response.data.keywords || []).map(tag => ({
        ...tag,
        originalStatus: tag.status
      }));
      setTags(tagsWithOriginal);
    } catch (err) {
      console.error('Error saving tags:', err);
      if (err.response?.data?.detail) {
        setError(`Failed to save tags: ${err.response.data.detail}`);
      } else {
        setError('Failed to save tags');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePurgeTags = async () => {
    try {
      setSaving(true);
      setError(''); // Clear any previous errors
      setShowPurgeDialog(false);
      
      // Get new tags (yellow) - those that are actually new or reactivated from DISABLED
      const newTags = tags.filter(tag => tag.isNew && tag.status === 'PENDING');
      // Get removed tags (orange) - only those that are actually marked for removal
      const removedTags = tags.filter(tag => tag.isRemoved && tag.status === 'DEACTIVATING');

      console.log('Purging tags:', { newTags, removedTags });

      // Add new tags first
      if (newTags.length > 0) {
        const newTagNames = newTags.map(tag => tag.name);
        console.log('Adding new tags:', newTagNames);
        await botAPI.addBotKeyword(botId, { keywords: newTagNames });
      }

      // Remove tags with purge=true
      if (removedTags.length > 0) {
        const removedTagNames = removedTags.map(tag => tag.name);
        console.log('Purging tags:', removedTagNames);
        await botAPI.removeBotKeyword(botId, { keywords: removedTagNames, purge: true });
      } else {
        // If no specific tags to remove, still call purge to clean up all disabled tags
        console.log('Purging all disabled tags');
        await botAPI.removeBotKeyword(botId, { keywords: [], purge: true });
      }

      // Refresh bot data
      const response = await botAPI.getBot(botId);
      setBot(response.data);
      // Add originalStatus to track the initial state
      const tagsWithOriginal = (response.data.keywords || []).map(tag => ({
        ...tag,
        originalStatus: tag.status
      }));
      setTags(tagsWithOriginal);
    } catch (err) {
      console.error('Error purging tags:', err);
      if (err.response?.data?.detail) {
        setError(`Failed to purge tags: ${err.response.data.detail}`);
      } else {
        setError('Failed to purge tags');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOwnerClick = () => {
    try { window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: bot.owner_id } })); } catch (_) {}
  };

  const handleAudienceClick = () => {
    if (bot.audience) {
      // BotAudienceResponse structure: { type: 'friend'|'group', id: int, name: str, avatar_id?: int }
      if (bot.audience.type === 'friend') {
        try { window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: bot.audience.id } })); } catch (_) {}
      } else if (bot.audience.type === 'group') {
        try { window.dispatchEvent(new CustomEvent('openGroup', { detail: { groupId: bot.audience.id } })); } catch (_) {}
      }
    } else if (bot.friend) {
      // Fallback to friend data
      try { window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: bot.friend.id } })); } catch (_) {}
    } else if (bot.group) {
      // Fallback to group data
      try { window.dispatchEvent(new CustomEvent('openGroup', { detail: { groupId: bot.group.id } })); } catch (_) {      }
    }
  };

  const handleSaveThemeVibes = async () => {
    try {
      setSavingThemeVibes(true);
      setError(''); // Clear any previous errors
      
      // Get new theme vibes (yellow) - those that are actually new or reactivated from DISABLED
      const newThemeVibes = themeVibes.filter(themeVibe => themeVibe.isNew && themeVibe.status === 'PENDING' && themeVibe.is_editable !== false);
      // Get removed theme vibes (orange) - only those that are actually marked for removal
      const removedThemeVibes = themeVibes.filter(themeVibe => themeVibe.isRemoved && themeVibe.status === 'DEACTIVATING' && themeVibe.is_editable !== false);

      console.log('Saving theme vibes:', { newThemeVibes, removedThemeVibes });

      // Add new theme vibes
      if (newThemeVibes.length > 0) {
        const newThemeVibeNames = newThemeVibes.map(themeVibe => themeVibe.name);
        console.log('Adding new theme vibes:', newThemeVibeNames);
        await botAPI.addBotThemeTone(botId, { theme_tones: newThemeVibeNames });
      }

      // Remove theme vibes
      if (removedThemeVibes.length > 0) {
        const removedThemeVibeNames = removedThemeVibes.map(themeVibe => themeVibe.name);
        console.log('Removing theme vibes:', removedThemeVibeNames);
        await botAPI.removeBotThemeTone(botId, { theme_tones: removedThemeVibeNames });
      }

      // Refresh bot data
      const response = await botAPI.getBot(botId);
      setBot(response.data);
      // Add originalStatus to track the initial state
      const themeVibesWithOriginal = (response.data.theme_tones || []).map(themeVibe => ({
        ...themeVibe,
        originalStatus: themeVibe.status
      }));
      setThemeVibes(themeVibesWithOriginal);
    } catch (err) {
      console.error('Error saving theme vibes:', err);
      if (err.response?.data?.detail) {
        setError(`Failed to save theme vibes: ${err.response.data.detail}`);
      } else {
        setError('Failed to save theme vibes');
      }
    } finally {
      setSavingThemeVibes(false);
    }
  };

  const handlePurgeThemeVibes = async () => {
    try {
      setSavingThemeVibes(true);
      setError(''); // Clear any previous errors
      setShowPurgeThemeVibesDialog(false);
      
      // Get new theme vibes (yellow) - those that are actually new or reactivated from DISABLED
      const newThemeVibes = themeVibes.filter(themeVibe => themeVibe.isNew && themeVibe.status === 'PENDING');
      // Get removed theme vibes (orange) - only those that are actually marked for removal
      const removedThemeVibes = themeVibes.filter(themeVibe => themeVibe.isRemoved && themeVibe.status === 'DEACTIVATING');

      console.log('Purging theme vibes:', { newThemeVibes, removedThemeVibes });

      // Add new theme vibes first
      if (newThemeVibes.length > 0) {
        const newThemeVibeNames = newThemeVibes.map(themeVibe => themeVibe.name);
        console.log('Adding new theme vibes:', newThemeVibeNames);
        await botAPI.addBotThemeTone(botId, { theme_tones: newThemeVibeNames });
      }

      // Remove theme vibes with purge=true
      if (removedThemeVibes.length > 0) {
        const removedThemeVibeNames = removedThemeVibes.map(themeVibe => themeVibe.name);
        console.log('Purging theme vibes:', removedThemeVibeNames);
        await botAPI.removeBotThemeTone(botId, { theme_tones: removedThemeVibeNames, purge: true });
      } else {
        // If no specific theme vibes to remove, still call purge to clean up all disabled theme vibes
        console.log('Purging all disabled theme vibes');
        await botAPI.removeBotThemeTone(botId, { theme_tones: [], purge: true });
      }

      // Refresh bot data
      const response = await botAPI.getBot(botId);
      setBot(response.data);
      // Add originalStatus to track the initial state
      const themeVibesWithOriginal = (response.data.theme_tones || []).map(themeVibe => ({
        ...themeVibe,
        originalStatus: themeVibe.status
      }));
      setThemeVibes(themeVibesWithOriginal);
    } catch (err) {
      console.error('Error purging theme vibes:', err);
      if (err.response?.data?.detail) {
        setError(`Failed to purge theme vibes: ${err.response.data.detail}`);
      } else {
        setError('Failed to purge theme vibes');
      }
    } finally {
      setSavingThemeVibes(false);
    }
  };

  const formatSchedule = (schedule) => {
    if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
      return null;
    }

    // If all 7 days are present, it runs every day
    if (schedule.length === 7 && schedule.every(day => day >= 0 && day <= 6)) {
      return "Runs every day.";
    }

    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const sortedDays = [...schedule].sort((a, b) => a - b);
    const dayNamesList = sortedDays.map(day => dayNames[day]).filter(Boolean);
    
    if (dayNamesList.length === 0) {
      return null;
    }

    if (dayNamesList.length === 1) {
      return `Runs ${dayNamesList[0]}.`;
    }

    if (dayNamesList.length === 2) {
      return `Runs ${dayNamesList[0]} and ${dayNamesList[1]}.`;
    }

    // For 3 or more days, use comma separation with "and" before the last day
    const lastDay = dayNamesList.pop();
    return `Runs ${dayNamesList.join(', ')}, and ${lastDay}.`;
  };

  if (loading) {
    return <div className="card">Loading...</div>;
  }

  if (error) {
    return (
      <div className="card">
        <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>
        <button onClick={handleBack} className="btn">Back</button>
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="card">
        <div style={{ color: 'red', marginBottom: '20px' }}>Bot not found</div>
        <button onClick={handleBack} className="btn">Back</button>
      </div>
    );
  }

  return (
    <div className="card">

      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <div style={{ flexShrink: 0 }}>
          <img 
            src={`/assets/${bot.avatar_id}`} 
            alt={`${bot.name} avatar`}
            style={{ 
              width: '120px', 
              height: '120px', 
              borderRadius: '50%',
              border: '2px solid #ddd'
            }} 
          />
        </div>
        
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: '0 0 10px 0' }}>{bot.name}</h1>
          <p style={{ fontStyle: 'italic', color: '#666', margin: '0 0 20px 0' }}>
            {(() => {
              if (bot.type === 'POSITION_BASED') {
                return "Position-based bots belong to friends or groups, and will generate stacks on a cadence (usually daily), using previous positions from all of the visible friend/group stacks.";
              } else if (bot.type === 'USER_DRIVEN') {
                return "User-driven bots allow users of a friendship or group to drive generated stacks. Can be run once a day.";
              } else if (bot.type === 'DATA_SET') {
                return "Data Set bots use a curated group of positions that share a set theme. These positions support URLs, an affinity or preference for where on the stack they go (and thus their preferred color), and are associated with special tags for filter options. They can run on schedules, and can also be triggered. From the Recent Runs section, simply choose what parameters you would like and then press Run. Data Set bots can be run around once a day.";
              }
              return "";
            })()}
          </p>
          
          {(bot.audience || bot.friend || bot.group) ? (
            <div style={{ marginBottom: '10px' }}>
              <strong>Audience: </strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <button 
                  onClick={handleAudienceClick}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#2196f3', 
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  {bot.audience ? (
                    // BotAudienceResponse structure: { type: 'friend'|'group', id: int, name: str, avatar_id?: int }
                    bot.audience.type === 'friend' ? (
                      <>
                        <img 
                          src={getDiceBearUserAvatar(bot.audience.name, bot.audience.avatar_id ?? 1)} 
                          alt={`${bot.audience.name} avatar`}
                          style={getAvatarStyle('medium')}
                        />
                        {bot.audience.name}
                      </>
                    ) : bot.audience.type === 'group' ? (
                      <>
                        <img 
                          src={getDiceBearGroupAvatar(bot.audience.name)} 
                          alt={`${bot.audience.name} avatar`}
                          style={getAvatarStyle('medium')}
                        />
                        {bot.audience.name}
                      </>
                    ) : null
                  ) : bot.friend ? (
                    // Fallback to friend data
                    <>
                      <img 
                        src={getDiceBearUserAvatar(bot.friend.username, bot.friend.avatar_id)} 
                        alt={`${bot.friend.username} avatar`}
                        style={getAvatarStyle('medium')}
                      />
                      {bot.friend.username}
                    </>
                  ) : bot.group ? (
                    // Fallback to group data
                    <>
                      <img 
                        src={getDiceBearGroupAvatar(bot.group.name)} 
                        alt={`${bot.group.name} avatar`}
                        style={getAvatarStyle('medium')}
                      />
                      {bot.group.name}
                    </>
                  ) : null}
                </button>
                
                {/* Show owner if there's a friend defined and it's not a group bot */}
                {((bot.audience && bot.audience.type === 'friend') || bot.friend) && !(bot.audience?.type === 'group' || bot.group) && (
                  <>
                    <span style={{ color: '#666' }}>•</span>
                    <button 
                      onClick={handleOwnerClick}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#2196f3', 
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      <img 
                        src={getDiceBearUserAvatar(bot.owner.username, bot.owner.avatar_id)} 
                        alt={`${bot.owner.username} avatar`}
                        style={getAvatarStyle('medium')}
                      />
                      {bot.owner.username}
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '10px' }}>
              <strong>Audience: </strong>
              <span style={{ color: '#666' }}>No audience configured</span>
            </div>
          )}
        </div>
      </div>

      {bot.bio && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontStyle: 'italic', color: '#666' }}>{bot.bio}</p>
        </div>
      )}

      {/* Operation Detail Dialog */}
      {operationDialogOpen && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '640px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Operation Detail</h3>
              <button onClick={handleCloseOperationDialog} className="btn">
                Close
              </button>
            </div>
            {operationDialogLoading ? (
              <div style={{ color: '#666', fontSize: '14px' }}>Loading operation...</div>
            ) : operationDialogError ? (
              <div style={{ color: '#f44336', fontSize: '14px' }}>{operationDialogError}</div>
            ) : operationDetail ? (
              <div style={{ fontSize: '14px', overflowY: 'auto', flex: 1 }}>
                <div style={{ marginBottom: '10px', color: '#666' }}>
                  <strong>Run:</strong> {formatDateTime(operationDetail.created_at)}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Status: </strong>
                  {renderOperationStatus(operationDetail.status)}
                </div>
                {canFailOperation && (
                  <div style={{ marginBottom: '12px' }}>
                    {operationFailError && (
                      <div style={{ color: '#f44336', marginBottom: '6px' }}>
                        {operationFailError}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleFailOperation}
                      className="btn"
                      disabled={operationFailLoading}
                      style={{
                        backgroundColor: '#ff9800',
                        color: '#fff',
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        cursor: operationFailLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {operationFailLoading ? 'Failing...' : 'Fail Job'}
                    </button>
                  </div>
                )}
                {operationDetail.bot_response && (
                  <>
                    <div style={{ marginBottom: '10px', color: '#666' }}>
                      <strong>Runtime:</strong>{' '}
                      {operationDetail.bot_response.runtime
                        ? `${operationDetail.bot_response.runtime.toFixed(2)} seconds`
                        : 'N/A'}
                    </div>
                  </>
                )}
                {operationDetail.keywords_prompt && operationDetail.keywords_prompt.length > 0 && (
                  <Keywords keywords={operationDetail.keywords_prompt} label="Tags Used:" />
                )}
                {operationDetail.request?.theme_tone && (
                  <div style={{ marginBottom: '10px', textAlign: 'left' }}>
                    <strong>Theme Vibe:</strong>{' '}
                    <span style={{ fontWeight: 'bold', color: TEXT_COLORS.SUCCESS }}>
                      {operationDetail.request.theme_tone}
                    </span>
                  </div>
                )}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>Request:</strong>
                    {(() => {
                      const requestData =
                        (operationDetail.request && Object.keys(operationDetail.request).length > 0 && operationDetail.request) ||
                        operationDetail.payload;
                      if (!requestData) {
                        return <p style={{ color: '#999', margin: 0 }}>Request body not recorded.</p>;
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => setOperationRequestExpanded(prev => !prev)}
                          className="btn"
                          style={{
                            backgroundColor: 'var(--color-border)',
                            color: 'var(--color-text)',
                            padding: '2px 8px',
                            fontSize: '12px',
                          }}
                        >
                          {operationRequestExpanded ? 'Hide' : 'Show'}
                        </button>
                      );
                    })()}
                  </div>
                  {operationRequestExpanded && (() => {
                    const requestData =
                      (operationDetail.request && Object.keys(operationDetail.request).length > 0 && operationDetail.request) ||
                      operationDetail.payload;
                    if (!requestData) {
                      return null;
                    }
                    return (
                      <pre
                        style={{
                          color: 'var(--color-text-muted)',
                          fontSize: '13px',
                          marginTop: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background: 'var(--color-background)',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid var(--color-border)',
                          maxHeight: '150px',
                          overflow: 'auto',
                          textAlign: 'left',
                        }}
                      >
                        {JSON.stringify(requestData, null, 2)}
                      </pre>
                    );
                  })()}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>Response:</strong>
                    {(() => {
                      const responseData =
                        (operationDetail.bot_response && operationDetail.bot_response.content
                          ? operationDetail.bot_response.content
                          : null) || operationDetail.response;
                      if (!responseData) {
                        return <p style={{ color: '#999', margin: 0 }}>Response payload not captured for this operation.</p>;
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => setOperationResponseExpanded(prev => !prev)}
                          className="btn"
                          style={{
                            backgroundColor: 'var(--color-border)',
                            color: 'var(--color-text)',
                            padding: '2px 8px',
                            fontSize: '12px',
                          }}
                        >
                          {operationResponseExpanded ? 'Hide' : 'Show'}
                        </button>
                      );
                    })()}
                  </div>
                  {operationResponseExpanded && (() => {
                    const responseData =
                      (operationDetail.bot_response && operationDetail.bot_response.content
                        ? operationDetail.bot_response.content
                        : null) || operationDetail.response;
                    if (!responseData) {
                      return null;
                    }
                    return (
                      <pre
                        style={{
                          color: 'var(--color-text-muted)',
                          fontSize: '13px',
                          marginTop: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background: 'var(--color-background)',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid var(--color-border)',
                          maxHeight: '150px',
                          overflow: 'auto',
                          textAlign: 'left',
                        }}
                      >
                        {JSON.stringify(responseData, null, 2)}
                      </pre>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div style={{ color: '#666' }}>Operation not found.</div>
            )}
          </div>
        </div>
      )}

      {operationRunDialogOpen && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '520px', width: '95%' }}>
            <h3 className="modal-title">Execute Bot</h3>
            {bot?.type === 'DATA_SET' ? (
              <>
                <p style={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                  Run with optional custom parameters. Give the stack a name and control how the positions show with transformation. Uncheck data sets and filters to hone in specifics. Finally use affinity matching to make the text better match the color of the position.
                </p>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ flex: '1' }}>
                      <label className="form-label">Stack Name</label>
                      <input
                        type="text"
                        value={operationStackName}
                        onChange={(e) => setOperationStackName(e.target.value)}
                        placeholder="Enter stack name (optional)"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ced4da',
                          borderRadius: '6px',
                          fontSize: '14px',
                        }}
                      />
                    </div>
                    <div style={{ marginTop: '24px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={operationAffinityMatching}
                          onChange={(e) => setOperationAffinityMatching(e.target.checked)}
                          style={{ margin: 0 }}
                        />
                        <span>Affinity Matching</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '8px', fontSize: '15px' }}>Transformation</h4>
                  <select
                    value={operationTransformationId || (bot.transformations && bot.transformations.length > 0 ? bot.transformations[0].id : '')}
                    onChange={(e) => setOperationTransformationId(Number(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  >
                    {(bot.transformations || []).map((transformation) => (
                      <option key={transformation.id} value={transformation.id}>
                        {transformation.name}
                      </option>
                    ))}
                  </select>
                  {operationTransformationId && (() => {
                    const selectedTransformation = (bot.transformations || []).find(t => t.id === operationTransformationId);
                    return selectedTransformation ? (
                      <p style={{ 
                        color: '#666', 
                        fontSize: '13px', 
                        fontStyle: 'italic', 
                        marginTop: '6px',
                        marginBottom: 0
                      }}>
                        {selectedTransformation.description}
                      </p>
                    ) : null;
                  })()}
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '8px', fontSize: '15px' }}>Filters</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <h5 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Data Sets</h5>
                      <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '6px', padding: '8px' }}>
                        {(bot.data_sets || []).map((dataSet) => (
                          <label key={dataSet.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={operationSelectedDataSetIds.includes(dataSet.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setOperationSelectedDataSetIds([...operationSelectedDataSetIds, dataSet.id]);
                                } else {
                                  setOperationSelectedDataSetIds(operationSelectedDataSetIds.filter(id => id !== dataSet.id));
                                }
                              }}
                              style={{ margin: 0 }}
                            />
                            <span>{dataSet.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h5 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Keywords</h5>
                      <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '6px', padding: '8px' }}>
                        {(bot.keywords || []).filter(kw => kw.is_available).map((keyword) => (
                          <label key={keyword.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={operationSelectedFilterIds.includes(keyword.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setOperationSelectedFilterIds([...operationSelectedFilterIds, keyword.id]);
                                } else {
                                  setOperationSelectedFilterIds(operationSelectedFilterIds.filter(id => id !== keyword.id));
                                }
                              }}
                              style={{ margin: 0 }}
                            />
                            <span>{keyword.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label className="form-label">Stack Previews (1-8)</label>
                  <select
                    value={operationStackPreviews}
                    onChange={(e) => setOperationStackPreviews(Number(e.target.value) || 1)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #ced4da',
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((val) => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <p style={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                  Run with optional custom parameters. Tags influence how bot will produce content. Theme Vibe will influence the overall theme and feel of the stack.
                </p>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '8px', fontSize: '15px' }}>Tags</h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: '8px',
                }}
              >
                {operationKeywords.map((keyword, index) => (
                  <div key={`op-keyword-${index}`}>
                    <input
                      type="text"
                      value={keyword}
                      onChange={(e) =>
                        handleOperationKeywordChange(index, e.target.value)
                      }
                      placeholder={`Tag ${index + 1}`}
                      maxLength={64}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ced4da',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    />
                    <CharacterCounter current={keyword.length} max={64} />
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 120px' }}>
                <label className="form-label">Positions Set By Bot (0-6)</label>
                <select
                  value={operationOpenPositions ?? 0}
                  onChange={(e) =>
                    handleOperationOpenPositionsChange(e.target.value)
                  }
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #ced4da',
                  }}
                >
                  {[0, 1, 2, 3, 4, 5, 6].map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label className="form-label">Stack Previews (1-8)</label>
                <select
                  value={operationStackPreviews}
                  onChange={(e) =>
                    setOperationStackPreviews(Number(e.target.value) || 1)
                  }
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #ced4da',
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '12px', marginBottom: '16px' }}>
              <label className="form-label">Theme Vibe</label>
              <textarea
                value={operationThemeTone}
                onChange={(e) => setOperationThemeTone(e.target.value)}
                maxLength={200}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid #ced4da',
                  resize: 'vertical',
                }}
              />
              <CharacterCounter current={operationThemeTone.length} max={200} />
            </div>
              </>
            )}
            {operationRunError && (
              <div style={{ color: '#f44336', marginBottom: '10px' }}>
                {operationRunError}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={closeOperationRunDialog} disabled={operationRunLoading}>
                Cancel
              </button>
              <button
                className="btn"
                onClick={handleRunOperation}
                disabled={operationRunLoading}
                style={{ backgroundColor: '#4caf50', color: '#fff' }}
              >
                {operationRunLoading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {bot.data && bot.data.schedule && formatSchedule(bot.data.schedule) && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Schedule</h3>
          <p style={{ color: '#666', fontSize: '14px' }}>
            {formatSchedule(bot.data.schedule)}
          </p>
        </div>
      )}

      {/* Operation Summary Section */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ margin: 0 }}>Recent Runs</h3>
          <button
            onClick={async () => {
              await loadOperations();
              await refreshBot();
            }}
            className="btn"
            disabled={operationsLoading}
            style={{
              backgroundColor: BUTTON_COLORS.SECONDARY,
              color: TEXT_COLORS.BUTTON_WHITE,
              padding: '6px 12px',
              borderRadius: '4px',
            }}
          >
            {operationsLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <>
          {operationsError && (
            <div style={{ color: '#f44336', fontSize: '14px', marginBottom: '10px' }}>
              {operationsError}
            </div>
          )}
          {operationsLoading ? (
            <div style={{ color: '#666', fontSize: '14px' }}>Loading operations...</div>
          ) : operations.length === 0 ? (
            <div style={{ color: '#666', fontSize: '14px' }}>
              No operations yet. Manual runs or scheduled jobs will appear here.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#666' }}>
                    <th style={{ padding: '8px 4px' }}>Run</th>
                    <th style={{ padding: '8px 4px' }}>Creator</th>
                    <th style={{ padding: '8px 4px' }}>Status</th>
                    <th style={{ padding: '8px 4px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {operations.map((operation) => (
                    <tr
                      key={operation.id}
                      style={{
                        borderTop: '1px solid #eee',
                      }}
                    >
                      <td style={{ padding: '10px 4px', color: '#333' }}>
                        {formatDateTime(operation.created_at)}
                      </td>
                      <td style={{ padding: '10px 4px' }}>
                        {operation.creator ? (
                          <button
                            onClick={() => handleCreatorClick(operation.creator.id)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              color: '#2196f3',
                            }}
                          >
                            <img
                              src={getDiceBearUserAvatar(
                                operation.creator.username || 'user',
                                operation.creator.avatar_id || 1
                              )}
                              alt={`${operation.creator.username} avatar`}
                              style={getAvatarStyle('small')}
                            />
                            {operation.creator.username || 'Unknown'}
                          </button>
                        ) : (
                          <span style={{ color: '#999' }}>System</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 4px' }}>
                        {renderOperationStatus(operation.status)}
                      </td>
                      <td style={{ padding: '10px 4px', textAlign: 'right' }}>
                        <button
                          onClick={() => handleOpenOperationDialog(operation.id)}
                          className="btn"
                          style={{
                            backgroundColor: 'transparent',
                            color: '#2196f3',
                            border: '1px solid #2196f3',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          View ↗
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {shouldShowExecuteButton && (
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button
                className="btn"
                onClick={openOperationRunDialog}
                style={{
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 18px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                }}
              >
                Run Now
              </button>
            </div>
          )}
        </>
      </div>

      {/* Positions Section - Show for admin users or bot owners, but hide for DATA_SET bots */}
      {currentUser && (currentUser.role === 'ADMIN' || currentUser.id === bot.owner_id) && bot.type !== 'DATA_SET' && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Positions</h3>
          <p style={{ color: TEXT_COLORS.SECONDARY, fontSize: '14px', marginBottom: '15px', fontStyle: 'italic' }}>
            {positions.length > 0 && positions[0].hasOwnProperty('array_affinity')
              ? 'Configure data set positions with weight and color affinity preferences.'
              : 'Help tailor bot runs by reviewing positions. Provide context for words that have shown up, and up or down vote to change the odds of them showing up.'
            }
          </p>
        {positionsLoading ? (
          <div style={{ color: '#666', fontSize: '14px' }}>
            Loading positions...
          </div>
        ) : positions.length > 0 ? (
          <div>
            {/* Mobile-first responsive grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              {positions.map((position, index) => (
                <div key={position.id || index} style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '16px',
                  boxShadow: 'none'
                }}>
                  {/* Position Name */}
                  <div style={{ 
                    marginBottom: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: TEXT_COLORS.MUTED,
                    lineHeight: '1.4'
                  }}>
                    {position.hasOwnProperty('array_affinity') ? position.name : position.position_name}
                  </div>
                  
                  {/* Context Input - Only for non-DATA_SET bots */}
                  {!position.hasOwnProperty('array_affinity') && (
                    <div style={{ marginBottom: '12px' }}>
                      <input
                        type="text"
                        value={position.context || ''}
                        onChange={(e) => handlePositionContextChange(index, e.target.value)}
                        placeholder="Enter context..."
                        maxLength={48}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ced4da',
                          borderRadius: '6px',
                          fontSize: '14px',
                          backgroundColor: '#fff',
                          boxSizing: 'border-box'
                        }}
                      />
                      <CharacterCounter current={(position.context || '').length} max={48} />
                    </div>
                  )}
                  
                  {/* Color Affinity - Only for DATA_SET bots */}
                  {position.hasOwnProperty('array_affinity') && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontStyle: 'italic', 
                        color: TEXT_COLORS.SECONDARY,
                        marginBottom: '8px'
                      }}>
                        Color affinity:
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '6px',
                        flexWrap: 'wrap'
                      }}>
                        {['red', 'orange', 'yellow', 'green', 'blue', 'purple'].map((color, colorIndex) => (
                          <label key={color} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            backgroundColor: position.array_affinity === colorIndex ? STACK_COLORS[colorIndex] : 'transparent',
                            border: `1px solid ${STACK_COLORS[colorIndex]}`,
                            transition: 'all 0.2s ease',
                            minWidth: '40px',
                            fontSize: '12px',
                            color: position.array_affinity === colorIndex ? 'white' : STACK_COLORS[colorIndex]
                          }}>
                            <input
                              type="radio"
                              name={`affinity-${index}`}
                              checked={position.array_affinity === colorIndex}
                              onChange={() => handlePositionAffinityChange(index, colorIndex)}
                              style={{ margin: 0 }}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Weight Options */}
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px',
                      justifyContent: 'center'
                    }}>
                      {position.hasOwnProperty('array_affinity') ? (
                        // DATA_SET bot weight options (0, 100, 300)
                        <>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            backgroundColor: position.weight === 0 ? BUTTON_COLORS.WEIGHT_SUPPRESS_SELECTED : 'transparent',
                            border: position.weight === 0 ? `1px solid ${BUTTON_COLORS.WEIGHT_SUPPRESS}` : `1px solid ${BUTTON_COLORS.WEIGHT_SUPPRESS}`,
                            transition: 'all 0.2s ease',
                            minWidth: '40px'
                          }}>
                            <input
                              type="radio"
                              name={`weight-${index}`}
                              checked={position.weight === 0}
                              onChange={() => handlePositionWeightChange(index, 0)}
                              style={{ margin: 0, marginRight: '4px' }}
                            />
                            <span style={{ fontSize: '20px' }}>🙅‍♀️</span>
                          </label>
                          
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            backgroundColor: position.weight === 100 ? BUTTON_COLORS.WEIGHT_STANDARD_SELECTED : 'transparent',
                            border: position.weight === 100 ? `1px solid ${BUTTON_COLORS.WEIGHT_STANDARD}` : `1px solid ${BUTTON_COLORS.WEIGHT_STANDARD}`,
                            transition: 'all 0.2s ease',
                            minWidth: '40px'
                          }}>
                            <input
                              type="radio"
                              name={`weight-${index}`}
                              checked={position.weight === 100}
                              onChange={() => handlePositionWeightChange(index, 100)}
                              style={{ margin: 0, marginRight: '4px' }}
                            />
                            <span style={{ fontSize: '20px' }}>💁‍♀️</span>
                          </label>
                          
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            backgroundColor: position.weight === 300 ? BUTTON_COLORS.WEIGHT_BOOST_SELECTED : 'transparent',
                            border: position.weight === 300 ? `1px solid ${BUTTON_COLORS.WEIGHT_BOOST}` : `1px solid ${BUTTON_COLORS.WEIGHT_BOOST}`,
                            transition: 'all 0.2s ease',
                            minWidth: '40px'
                          }}>
                            <input
                              type="radio"
                              name={`weight-${index}`}
                              checked={position.weight === 300}
                              onChange={() => handlePositionWeightChange(index, 300)}
                              style={{ margin: 0, marginRight: '4px' }}
                            />
                          <span style={{ fontSize: '20px' }}>🙋‍♀️</span>
                        </label>
                      </>
                    ) : (
                      // Regular bot weight options (20, 100, 250)
                        <>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            backgroundColor: position.weight === 20 ? BUTTON_COLORS.WEIGHT_SUPPRESS_SELECTED : 'transparent',
                            border: position.weight === 20 ? `1px solid ${BUTTON_COLORS.WEIGHT_SUPPRESS}` : `1px solid ${BUTTON_COLORS.WEIGHT_SUPPRESS}`,
                            transition: 'all 0.2s ease',
                            minWidth: '40px'
                          }}>
                            <input
                              type="radio"
                              name={`weight-${index}`}
                              checked={position.weight === 20}
                              onChange={() => handlePositionWeightChange(index, 20)}
                              style={{ margin: 0, marginRight: '4px' }}
                            />
                            <span style={{ fontSize: '20px' }}>🙅‍♀️</span>
                          </label>
                          
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            backgroundColor: position.weight === 100 ? BUTTON_COLORS.WEIGHT_STANDARD_SELECTED : 'transparent',
                            border: position.weight === 100 ? `1px solid ${BUTTON_COLORS.WEIGHT_STANDARD}` : `1px solid ${BUTTON_COLORS.WEIGHT_STANDARD}`,
                            transition: 'all 0.2s ease',
                            minWidth: '40px'
                          }}>
                            <input
                              type="radio"
                              name={`weight-${index}`}
                              checked={position.weight === 100}
                              onChange={() => handlePositionWeightChange(index, 100)}
                              style={{ margin: 0, marginRight: '4px' }}
                            />
                            <span style={{ fontSize: '20px' }}>💁‍♀️</span>
                          </label>
                          
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            backgroundColor: position.weight === 250 ? BUTTON_COLORS.WEIGHT_BOOST_SELECTED : 'transparent',
                            border: position.weight === 250 ? `1px solid ${BUTTON_COLORS.WEIGHT_BOOST}` : `1px solid ${BUTTON_COLORS.WEIGHT_BOOST}`,
                            transition: 'all 0.2s ease',
                            minWidth: '40px'
                          }}>
                            <input
                              type="radio"
                              name={`weight-${index}`}
                              checked={position.weight === 250}
                              onChange={() => handlePositionWeightChange(index, 250)}
                              style={{ margin: 0, marginRight: '4px' }}
                            />
                            <span style={{ fontSize: '20px' }}>🙋‍♀️</span>
                          </label>

                          {position.is_bot_generated && (
                            <label style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              backgroundColor: position.allow_bot ? BUTTON_COLORS.SURFACE_NEUTRAL_ACTIVE : BUTTON_COLORS.SURFACE_NEUTRAL,
                              border: `1px solid ${BUTTON_COLORS.SURFACE_NEUTRAL_BORDER}`,
                              transition: 'all 0.2s ease',
                              minWidth: '40px'
                            }}>
                              <input
                                type="checkbox"
                                checked={position.allow_bot || false}
                                onChange={(e) => handlePositionAllowBotChange(index, e.target.checked)}
                                style={{
                                  margin: 0,
                                  marginRight: '4px',
                                  transform: 'scale(1.1)'
                                }}
                              />
                              <span style={{ fontSize: '20px' }}>🤖</span>
                            </label>
                          )}
                      </>
                    )}
                  </div>
                </div>

                </div>
              ))}
            </div>
            
            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'flex-start'
            }}>
              <button
                onClick={handleSkipPositions}
                className="btn"
                style={{ 
                  backgroundColor: BUTTON_COLORS.PRIMARY, 
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (positionsLoading || positionsSaving) ? 'not-allowed' : 'pointer',
                  opacity: (positionsLoading || positionsSaving) ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                disabled={positionsLoading || positionsSaving}
              >
                Skip
              </button>
              
              <button
                onClick={handleSavePositions}
                className="btn"
                style={{ 
                  backgroundColor: BUTTON_COLORS.SAVE, 
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (positionsLoading || positionsSaving) ? 'not-allowed' : 'pointer',
                  opacity: (positionsLoading || positionsSaving) ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                disabled={positionsLoading || positionsSaving}
              >
                {positionsSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ 
            color: TEXT_COLORS.SECONDARY, 
            fontSize: '14px',
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'transparent',
            borderRadius: '8px',
            border: '1px solid #ddd'
          }}>
            No positions available.
          </div>
        )}
        </div>
      )}

      {/* Tags Section - Hide for DATA_SET bots */}
      {bot.type !== 'DATA_SET' && (
      <div style={{ marginBottom: '20px' }}>
        <h3>Tags</h3>
        <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
          <em>Add and remove tags to influence the way in which the bot will produce content. You can supply additional context for proper nouns in parentheses; e.g., "Felix (cat)" or "Garfield (cat)". Tags used in previous runs are shown in italic text, and will not be selected until all tags have been used.</em>
        </p>
        
        <div style={{ marginBottom: '10px' }}>
          <input
            type="text"
            value={tagInput}
            onChange={handleTagInputChange}
            onKeyDown={handleTagInputKeyDown}
            onBlur={handleTagInputBlur}
            placeholder="Type a tag name and press Tab or click outside to add"
            style={{
              width: '100%',
              padding: '8px',
              border: tagInputError ? '1px solid #f44336' : '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          {tagInputError && (
            <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
              {tagInputError}
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
          {tags.map(tag => (
            <div
              key={tag.id}
              style={getTagStyle(tag)}
            >
              <span>{tag.name}</span>
              {tag.is_editable !== false && (
                <button
                  onClick={() => removeTag(tag.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '0',
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={handleBack} 
            className="btn" 
            style={{ backgroundColor: '#2196f3', color: 'white' }}
          >
            Back
          </button>
          <button 
            onClick={handleSaveTags}
            disabled={saving}
            className="btn"
            style={{ backgroundColor: '#4caf50', color: 'white' }}
          >
            {saving ? 'Saving...' : 'Save Tags'}
          </button>
          {/* Show Purge button only if user is owner or bot is a friend bot */}
          {(currentUser && (bot.owner_id === currentUser.id || bot.friend_id)) && (
            <button 
              onClick={() => setShowPurgeDialog(true)}
              disabled={saving}
              className="btn"
              style={{ backgroundColor: '#f44336', color: 'white' }}
            >
              Purge
            </button>
          )}
        </div>
      </div>
      )}

      {/* Purge Confirmation Dialog */}
      {showPurgeDialog && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '400px' }}>
            <h3 className="modal-title">Purge Disabled Tags?</h3>
            <p className="modal-text">
              This will permanently remove all tags <span style={{ color: '#f44336', fontWeight: 'bold' }}>currently disabled</span>. 
              It will not affect tags that are <span style={{ color: '#4caf50', fontWeight: 'bold' }}>currently active</span>.
            </p>
            <div className="modal-actions">
              <button 
                onClick={() => setShowPurgeDialog(false)}
                className="btn"
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                onClick={handlePurgeTags}
                disabled={saving}
                className="btn"
                style={{ backgroundColor: '#f44336', color: 'white' }}
              >
                {saving ? 'Purging...' : 'Purge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Theme Vibes Section - Hide for DATA_SET bots */}
      {bot.type !== 'DATA_SET' && (
      <div style={{ marginBottom: '20px' }}>
        <h3>Theme Vibes</h3>
        <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
          <em>Add and remove theme vibes to influence the way in which the bot will produce a theme. Think adjectives like "funny" or "accurate".</em>
        </p>
        
        <div style={{ marginBottom: '10px' }}>
          <input
            type="text"
            value={themeVibeInput}
            onChange={handleThemeVibeInputChange}
            onKeyDown={handleThemeVibeInputKeyDown}
            onBlur={handleThemeVibeInputBlur}
            placeholder="Type a theme vibe name and press Tab or click outside to add"
            style={{
              width: '100%',
              padding: '8px',
              border: themeVibeInputError ? '1px solid #f44336' : '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          {themeVibeInputError && (
            <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
              {themeVibeInputError}
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
          {themeVibes.map(themeVibe => (
            <div
              key={themeVibe.id}
              style={getThemeVibeStyle(themeVibe)}
            >
              <span>{themeVibe.name}</span>
              {themeVibe.is_editable !== false && (
                <button
                  onClick={() => removeThemeVibe(themeVibe.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '0',
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={handleBack} 
            className="btn" 
            style={{ backgroundColor: '#2196f3', color: 'white' }}
          >
            Back
          </button>
          <button 
            onClick={handleSaveThemeVibes}
            disabled={savingThemeVibes}
            className="btn"
            style={{ backgroundColor: '#4caf50', color: 'white' }}
          >
            {savingThemeVibes ? 'Saving...' : 'Save Theme Vibes'}
          </button>
          {/* Show Purge button only if user is owner or bot is a friend bot */}
          {(currentUser && (bot.owner_id === currentUser.id || bot.friend_id)) && (
            <button 
              onClick={() => setShowPurgeThemeVibesDialog(true)}
              disabled={savingThemeVibes}
              className="btn"
              style={{ backgroundColor: '#f44336', color: 'white' }}
            >
              Purge
            </button>
          )}
        </div>
      </div>
      )}

      {/* Purge Theme Vibes Confirmation Dialog */}
      {showPurgeThemeVibesDialog && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '400px' }}>
            <h3 className="modal-title">Purge Disabled Theme Vibes?</h3>
            <p className="modal-text">
              This will permanently remove all theme vibes <span style={{ color: '#f44336', fontWeight: 'bold' }}>currently disabled</span>. 
              It will not affect theme vibes that are <span style={{ color: '#4caf50', fontWeight: 'bold' }}>currently active</span>.
            </p>
            <div className="modal-actions">
              <button 
                onClick={() => setShowPurgeThemeVibesDialog(false)}
                className="btn"
                disabled={savingThemeVibes}
              >
                Cancel
              </button>
              <button 
                onClick={handlePurgeThemeVibes}
                disabled={savingThemeVibes}
                className="btn"
                style={{ backgroundColor: '#f44336', color: 'white' }}
              >
                {savingThemeVibes ? 'Purging...' : 'Purge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BotProfile;
